// Shared course types and helper functions used by both JDS and NextGen domains.

export interface CourseAttachment {
  name: string;
  url: string;
  size: number;
  type: string;
}

export interface CourseLesson {
  id: string;
  title: string;
  content: string | null;
  video_id: string | null;
  position: number;
  status: 'Draft' | 'Coming Soon' | 'Published' | 'Archived';
  attachments?: CourseAttachment[];
  module_id: string;
}

export interface CourseModule {
  id: string;
  title: string;
  description: string | null;
  position: number;
  lessons: CourseLesson[];
}

export interface Course {
  id: string;
  title: string;
  overview: string | null;
  tile_description: string | null;
  course_type: string;
  is_featured: boolean;
  status: string;
  skill_domains: string[] | null;
  lesson_type: string | null;
  modules: CourseModule[];
}

export function getCourseProgress(
  course: Course,
  completedLessons: Record<string, boolean | string | null>
): { total: number; done: number; percent: number } {
  const publishedLessons = course.modules
    .flatMap((m) => m.lessons)
    .filter((l) => l.status === 'Published');
  const total = publishedLessons.length;
  const done = publishedLessons.filter((l) => !!completedLessons[l.id]).length;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  return { total, done, percent };
}

export function getModuleLessonCounts(course: Course): {
  modules: number;
  lessons: number;
} {
  return {
    modules: course.modules.length,
    lessons: course.modules.reduce((acc, m) => acc + m.lessons.length, 0),
  };
}

export function getPublishedLessonsFlat(course: Course): CourseLesson[] {
  return course.modules
    .flatMap((m) => m.lessons)
    .filter((l) => l.status === 'Published');
}

/**
 * Returns ALL lessons regardless of status, flattened across modules and
 * sorted by module.position then lesson.position. Used by admin edit-mode
 * to surface drafts/archived/coming-soon lessons in the player TOC.
 */
export function getAllLessonsFlat(course: Course): CourseLesson[] {
  return [...course.modules]
    .sort((a, b) => a.position - b.position)
    .flatMap((m) =>
      [...m.lessons].sort((a, b) => a.position - b.position)
    );
}

export function findModuleForLesson(
  course: Course,
  lessonId: string
): CourseModule | undefined {
  return course.modules.find((m) => m.lessons.some((l) => l.id === lessonId));
}

export function getAdjacentLessons(
  course: Course,
  currentLessonId: string,
  includeAll: boolean = false
): { previous: CourseLesson | null; next: CourseLesson | null } {
  const flat = includeAll
    ? getAllLessonsFlat(course)
    : getPublishedLessonsFlat(course);
  const idx = flat.findIndex((l) => l.id === currentLessonId);
  return {
    previous: idx > 0 ? flat[idx - 1] : null,
    next: idx < flat.length - 1 ? flat[idx + 1] : null,
  };
}
